import { useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, FlaskConical, Pencil, Plus, ScrollText, Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { Separator } from '@/components/ui/separator'
import { evaluate } from '@/lib/expr'
import { describeCondition } from '../../components/ConditionEditor'
import FormRenderer from '../../components/FormRenderer'
import { SeverityBadge } from '../../components/badges'
import { activePages } from '../../lib/form'
import type { Answers, FormDefinition, FormRule } from '../../types'
import RuleEditor from './RuleEditor'
import { addRule, allFields, moveRule, nextRuleId, removeRule, updateRule } from './model'

interface Props {
    def: FormDefinition
    edit: (fn: (d: FormDefinition) => FormDefinition) => void
}

/** Sample answers in, fired rules out, using the real expression evaluator. */
function RuleTester({ def }: { def: FormDefinition }) {
    const [answers, setAnswers] = useState<Answers>({})
    const pages = activePages(def, answers)
    const fired = def.rules.filter((r) => {
        try { return evaluate(r.when, answers) } catch { return false }
    })
    return (
        <Collapsible>
            <Card size="sm">
                <CardHeader>
                    <CardTitle>Test rules</CardTitle>
                    <CardDescription>Fill in sample answers to see which rules would fire. Nothing is saved.</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col gap-4">
                    <div className="flex flex-wrap items-center gap-2">
                        <CollapsibleTrigger asChild>
                            <Button variant="outline" size="sm"><FlaskConical data-icon="inline-start" /> Show sample form</Button>
                        </CollapsibleTrigger>
                        <Button variant="ghost" size="sm" onClick={() => setAnswers({})}>Clear answers</Button>
                        <Badge variant={fired.length ? 'default' : 'secondary'}>{fired.length} of {def.rules.length} fire</Badge>
                    </div>
                    <div role="status" aria-live="polite" className="flex flex-col gap-2">
                        {fired.length === 0 ? (
                            <p className="text-sm text-muted-foreground">No rules fire for these answers.</p>
                        ) : fired.map((r) => (
                            <div key={r.id} className="flex items-start gap-2 rounded-lg border p-2 text-sm">
                                <SeverityBadge severity={r.severity} />
                                <span className="min-w-0 flex-1">{r.message}</span>
                                <code className="font-mono text-xs text-muted-foreground">{r.id}</code>
                            </div>
                        ))}
                    </div>
                    <CollapsibleContent className="flex flex-col gap-6">
                        <Separator />
                        {pages.map((p) => (
                            <section key={p.id} className="flex flex-col gap-3" aria-label={p.title}>
                                <h3 className="text-sm font-semibold">{p.title}</h3>
                                <FormRenderer page={p} answers={answers} onChange={(k, v) => setAnswers((a) => ({ ...a, [k]: v }))} />
                            </section>
                        ))}
                    </CollapsibleContent>
                </CardContent>
            </Card>
        </Collapsible>
    )
}

export default function RulesTab({ def, edit }: Props) {
    const fields = useMemo(() => allFields(def), [def])
    const [editing, setEditing] = useState<{ index: number | null; rule: FormRule; nonce: number } | null>(null)
    const [confirm, setConfirm] = useState<number | null>(null)

    const startNew = () => setEditing({
        index: null, nonce: Date.now(),
        rule: { id: nextRuleId(def.rules), severity: 'medium', message: '', when: { all: [] } },
    })
    const startEdit = (i: number) => setEditing({ index: i, rule: def.rules[i], nonce: Date.now() })
    const save = (r: FormRule) => {
        if (!editing) return
        if (editing.index === null) edit((d) => addRule(d, r))
        else edit((d) => updateRule(d, editing.index as number, r))
        setEditing(null)
    }

    return (
        <div className="flex flex-col gap-4">
            <Card size="sm">
                <CardHeader>
                    <CardTitle>Rules</CardTitle>
                    <CardDescription>Findings raised for reviewers when answers match. Rules run in this order.</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col gap-3">
                    {def.rules.length === 0 ? (
                        <Empty className="border border-dashed">
                            <EmptyHeader>
                                <EmptyMedia variant="icon"><ScrollText /></EmptyMedia>
                                <EmptyTitle>No rules yet</EmptyTitle>
                                <EmptyDescription>Rules turn answers into findings, such as flagging a high-risk data type.</EmptyDescription>
                            </EmptyHeader>
                            <EmptyContent>
                                <Button size="sm" onClick={startNew} disabled={fields.length === 0}><Plus data-icon="inline-start" /> Add the first rule</Button>
                            </EmptyContent>
                        </Empty>
                    ) : (
                        <>
                            <ul className="flex flex-col gap-2" aria-label="Rules">
                                {def.rules.map((r, i) => (
                                    <li key={i} className="flex items-start gap-1 rounded-lg border p-2">
                                        <div className="flex min-w-0 flex-1 flex-col gap-1 px-1">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <SeverityBadge severity={r.severity} />
                                                <code className="font-mono text-xs text-muted-foreground">{r.id}</code>
                                                {r.section && <Badge variant="outline">{r.section}</Badge>}
                                            </div>
                                            <p className="text-sm font-medium">{r.message || 'No message'}</p>
                                            <p className="text-xs text-muted-foreground">When: {describeCondition(r.when, fields)}</p>
                                        </div>
                                        <Button variant="ghost" size="icon-xs" aria-label={`Move rule ${r.id} up`} disabled={i === 0} onClick={() => edit((d) => moveRule(d, i, -1))}><ArrowUp /></Button>
                                        <Button variant="ghost" size="icon-xs" aria-label={`Move rule ${r.id} down`} disabled={i === def.rules.length - 1} onClick={() => edit((d) => moveRule(d, i, 1))}><ArrowDown /></Button>
                                        <Button variant="ghost" size="icon-xs" aria-label={`Edit rule ${r.id}`} onClick={() => startEdit(i)}><Pencil /></Button>
                                        <Button variant="ghost" size="icon-xs" aria-label={`Delete rule ${r.id}`} onClick={() => setConfirm(i)}><Trash2 /></Button>
                                    </li>
                                ))}
                            </ul>
                            <div><Button size="sm" onClick={startNew}><Plus data-icon="inline-start" /> Add rule</Button></div>
                        </>
                    )}
                </CardContent>
            </Card>

            {def.rules.length > 0 && <RuleTester def={def} />}

            {editing && (
                <RuleEditor
                    key={editing.nonce}
                    open
                    initial={editing.rule}
                    isNew={editing.index === null}
                    otherIds={new Set(def.rules.filter((_, i) => i !== editing.index).map((r) => r.id))}
                    fields={fields}
                    onSave={save}
                    onClose={() => setEditing(null)}
                />
            )}

            <Dialog open={confirm !== null} onOpenChange={(o) => { if (!o) setConfirm(null) }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete rule {confirm !== null ? def.rules[confirm]?.id : ''}?</DialogTitle>
                        <DialogDescription>Findings from this rule stop being raised for new submissions after you publish.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirm(null)}>Cancel</Button>
                        <Button variant="destructive" onClick={() => { if (confirm !== null) edit((d) => removeRule(d, confirm)); setConfirm(null) }}>Delete rule</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
